import React from 'react';
import { Routes, Route, Navigate, Link } from 'react-router-dom';
import { AppProvider } from './context/AppContext';
import { useAuth } from './context/AuthContext';
import Login from './components/Login';
import Register from './components/Register';
import VerifyEmail from './components/VerifyEmail';
import VerifyWhatsApp from './components/VerifyWhatsApp';
import AdminUserManagement from './components/AdminUserManagement';
import ChangePassword from './components/ChangePassword';
import Dashboard from './components/Dashboard';
import AnggotaList from './components/AnggotaList';
import SetoranForm from './components/SetoranForm';

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole = null }) => {
  const { token, user, loading } = useAuth();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-gray-600">Memuat sesi...</div>;
  }

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole && user?.role !== requiredRole) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Layout untuk authenticated users
function AppLayout({ children }) {
  const { user, logout, isLoggedIn } = useAuth();

  if (!isLoggedIn) {
    return children;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-indigo-700 text-white p-3">
        <div className="container mx-auto flex flex-col gap-3 md:flex-row md:justify-between md:items-center">
          <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-4 w-full md:w-auto">
            <Link to="/dashboard" className="hover:underline text-base sm:text-lg font-bold text-yellow-300 text-center sm:text-left">Dashboard</Link>
            <Link to="/anggota" className="hover:underline text-base sm:text-lg font-bold text-yellow-300 text-center sm:text-left">Anggota</Link>
            <Link to="/setoran" className="hover:underline text-base sm:text-lg font-bold text-yellow-300 text-center sm:text-left">Setoran</Link>
            {user?.role === 'superadmin' && (
              <Link to="/admin/users" className="hover:underline font-bold text-sm sm:text-base text-center sm:text-left">Admin Panel</Link>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:gap-3 md:gap-4 w-full md:w-auto justify-between md:justify-end">
            <span className="text-xs sm:text-sm truncate max-w-[170px] sm:max-w-none">Welcome, {user?.username} ({user?.role})</span>
            <Link
              to="/change-password"
              className="text-xs sm:text-sm bg-blue-600 hover:bg-blue-700 px-2 sm:px-3 py-1 rounded transition"
              title="Ubah password"
            >
              🔒 Ubah Password
            </Link>
            <button
              onClick={logout}
              className="text-xs sm:text-sm bg-red-600 hover:bg-red-700 px-2 sm:px-3 py-1 rounded"
            >
              Logout
            </button>
          </div>
        </div>
      </nav>
      <div className="container mx-auto p-4">
        {children}
      </div>
    </div>
  );
}

function App() {
  const { isLoggedIn, loading } = useAuth();

  return (
    <AppProvider>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/verify-whatsapp" element={<VerifyWhatsApp />} />

        {/* Protected Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Dashboard />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/anggota"
          element={
            <ProtectedRoute>
              <AppLayout>
                <AnggotaList />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/setoran"
          element={
            <ProtectedRoute>
              <AppLayout>
                <SetoranForm />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Superadmin Routes */}
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute requiredRole="superadmin">
              <AppLayout>
                <AdminUserManagement />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Change Password Route */}
        <Route
          path="/change-password"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ChangePassword />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Root - Redirect to dashboard if logged in, else to login */}
        <Route
          path="/"
          element={
            loading ? (
              <div className="min-h-screen flex items-center justify-center text-gray-600">Memuat sesi...</div>
            ) : isLoggedIn ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* 404 - Redirect based on auth status */}
        <Route
          path="*"
          element={
            loading ? (
              <div className="min-h-screen flex items-center justify-center text-gray-600">Memuat sesi...</div>
            ) : (
              <Navigate to={isLoggedIn ? "/dashboard" : "/login"} replace />
            )
          }
        />
      </Routes>
    </AppProvider>
  );
}

export default App;